import AuthoringExperiments from 'bundles/epic/clients/Authoring';
import { PLATFORM_ONLY_ITEM_TYPES } from 'bundles/item-authoring/shared/platformOnlyItemTypes';
import type { ItemType } from 'bundles/item/types/ItemType';

/**
 * Function to gate item type migrations onto the item platform / item authoring framework
 */
export const isItemAuthoringFrameworkEnabledForType = (itemType: ItemType): boolean => {
  if (!itemType) {
    return false;
  }

  // 1. [SETUP] set up and add your own item-type-specific feature flag check here while in development (ie requires user/cookie targeting)
  // example epic where we gate by item type: https://tools.coursera.org/epic/experiment/HJG-kI6uEeyAw9c5depHlw
  // when use something like AuthoringExperiments.get('yourEpicName').includes?.(itemType)

  // 2. [CLEANUP] once your item type migration is completed, please clean up your feature flag check and
  // add your item to the static list referenced below.
  if (PLATFORM_ONLY_ITEM_TYPES.includes(itemType)) {
    return true;
  }
  return false;
};

// https://tools.coursera.org/epic/experiment/CH1RJ_xEEe666w76OVbGYQ
export const isPluginUpdatedUiEnabled = () => {
  return AuthoringExperiments.get('enablePluginUpdatedUi');
};

// https://tools.coursera.org/epic/experiment/mc7TwyjNEe-BZw7QjuOi1Q
export const isV2PluginItemAuthoringEnabled = () => {
  return AuthoringExperiments.get('enableV2PluginItemAuthoring');
};

// https://tools.coursera.org/epic/experiment/iG7-ZVamEe-USQr_6ZvHVQ
export const isCourseOutlineSummaryExportsEnabled = () => {
  return AuthoringExperiments.get('enableCourseOutlineSummaryExports');
};

// https://tools.coursera.org/epic/experiment/z3n_v4aWEe-yzhJWa8b7XQ
export const isCoachForAuthorsEnabled = () => {
  return AuthoringExperiments.get('enableCoachForAuthors');
};

// https://tools.coursera.org/epic/experiment/LCpPSJSaEfC-lAr_4rnFJw (for GA)
export const isCommonCartridgeSmartIngestionLaunchEnabled = () => {
  return AuthoringExperiments.get('enableCommonCartridgeSmartIngestionLaunch');
};

// https://tools.coursera.org/epic/experiment/mlXFx4v3Ee-yzhJWa8b7XQ (for pilot with partnerId)
export const isCommonCartridgeSmartIngestionEnabled = () => {
  return (
    AuthoringExperiments.get('enableCommonCartridgeSmartIngestion') || isCommonCartridgeSmartIngestionLaunchEnabled()
  );
};

// https://tools.coursera.org/epic/experiment/H64lR1dyEfCBFBJ2BJ8jWQ
export const isDescriptEnabledByPartnerId = (partnerId?: number) => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }

  return AuthoringExperiments.get('enableDescriptIntegrationByPartnerId', { partner_id: partnerId.toString() });
};

// https://tools.coursera.org/epic/experiment/l8-MNuTLEe-6kQ5XAizh6w
export const isDescriptIntegrationEnabled = (partnerId?: number) => {
  return isDescriptEnabledByPartnerId(partnerId);
};

export const isDescriptIntegrationForRedirect = () => {
  return AuthoringExperiments.get('enableDescriptIntegration');
};

// https://tools.coursera.org/epic/experiment/Bj6atwZ9EfC1iRKne8QPDw
export const isCourseBuilderNonEnglishSupportEnabled = (partnerId?: number) => {
  return AuthoringExperiments.get('enableCourseBuilderNonEnglishSupport');
};

export const isGraphqlCreateAuthoringAssetCreationEnabled = () => {
  return AuthoringExperiments.get('enableGraphQlCreateAuthoringAssetCreationAttempt');
};

// https://tools.coursera.org/epic/experiment/s9lI3W48EfC3IA6hNZeg9Q
export const isCoachForSmartIngestionEnabled = () => {
  return AuthoringExperiments.get('enableCoachForSmartIngestion');
};

export const isGradedPluginEnabled = () => {
  return AuthoringExperiments.get('enableGradedPlugin');
};

// note: this epic is intended to gate P0
// https://tools.coursera.org/epic/experiment/ez4yS6UiEfC-lAr_4rnFJw
export const isCbVerifiedSkillsP0EnabledForUser = () => {
  return AuthoringExperiments.get('enableCbVerifiedSkills');
};

// https://tools.coursera.org/epic/experiment/zTCtqLkQEfCAfhJYNo68EQ
export const isCbVerifiedSkillsP0EnabledByPartnerId = (partnerId?: number) => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }
  return AuthoringExperiments.get('enableCbVerifiedSkillsP0ByPartnerId', { partner_id: partnerId.toString() });
};

// there was a bit of back and forth for this gating so i've just left to handle gating by user or partner id
// since we already had an existing epic to handle one but not the other.
export const isCbVerifiedSkillsP0Enabled = (partnerId?: number) => {
  return isCbVerifiedSkillsP0EnabledByPartnerId(partnerId) || isCbVerifiedSkillsP0EnabledForUser();
};

// https://tools.coursera.org/epic/experiment/zTCtqLkQEfCAfhJYNo68EQ
export const isCbVerifiedSkillsP1EnabledByPartnerId = (partnerId?: number) => {
  if (!partnerId && partnerId !== 0) {
    return false;
  }
  return AuthoringExperiments.get('enableCbVerifiedSkillsP1ByPartnerId', { partner_id: partnerId.toString() });
};
